import * as G6 from '@antv/g6';
import { Color } from '@persona-id/design-tokens';

import {
  COLLAPSED_GROUP_TYPE_G6_TYPE,
  COLLAPSED_PAIRWISE_NODE_TYPE_G6_TYPE,
  datumTypeTokenToG6Type,
  G6Type,
  PROPERTY_NODE_TYPE_G6_TYPE,
} from 'dashboard/components/GraphCanvasV2/G6Mapper';
import { VERTEX_ICON_SOURCES_V2 } from 'dashboard/lib/utils/graph';
import {
  CollapsedGroup,
  CollapsedPairwiseNode,
  DatumTypeToken,
  G6Edge,
  Node,
} from 'dashboard/models/graph';
import {
  ACCOUNT_DATUM_TYPE,
  G6_DIRECTED_EDGE,
  NODE_COLORS_V2,
  UNKNOWN_COLOR,
} from 'lib/constants/graph';

const getImageSize = (g6Type: G6Type) => {
  if (g6Type === datumTypeTokenToG6Type(ACCOUNT_DATUM_TYPE.token)) {
    return 24;
  }
  return 18;
};

const getOutlinedSize = (imageSize: number) => {
  return imageSize + 8;
};

const getRadius = (g6Type: G6Type) => {
  if (g6Type === datumTypeTokenToG6Type(ACCOUNT_DATUM_TYPE.token)) {
    return 12;
  }
  return 8;
};
const BADGE_RADIUS = 6;

const V2_ICON_SOURCES: Map<G6Type, string | null> = new Map(
  Object.entries(VERTEX_ICON_SOURCES_V2).map(([datumTypeToken, iconSource]) => {
    return [datumTypeTokenToG6Type(datumTypeToken), iconSource];
  }),
);

export interface G6DirectedEdgeModelConfig extends G6Edge {
  arrowSizeRadius: number;
}

export type NodeIconLookupFn = (token: DatumTypeToken) => string;

const COLLAPSED_GROUP_SIZE = 64;
const COLLAPSED_NODE_SIZE = 48;

export default (g6: typeof G6, nodeIconLookupFn: NodeIconLookupFn) => {
  g6.registerNode(
    'BaseNode',
    {
      options: {
        size: 24,
        style: {
          fill: UNKNOWN_COLOR,
          lineWidth: 0,
          radius: 8,
        },
      },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      afterDraw: (cfg: G6.NodeConfig, group: G6.IGroup) => {
        drawNode(cfg, group, nodeIconLookupFn);
        drawSelectedState(cfg, group);
        drawHighlightedState(cfg, group);
        drawLoadingState(cfg, group);
        drawExpandableState(cfg, group);
        drawExcludedState(cfg, group);
      },

      // Important: Ensures that the [after]draw effects still hold.
      // See: https://g6.antv.vision/en/docs/manual/middle/elements/nodes/custom-node for details.
      update: undefined,
    },
    'rect',
  );

  g6.registerNode(
    datumTypeTokenToG6Type(ACCOUNT_DATUM_TYPE.token),
    {
      options: {
        size: 32,
        style: {
          fill: NODE_COLORS_V2[ACCOUNT_DATUM_TYPE.token] ?? UNKNOWN_COLOR,
          lineWidth: 0,
          radius: 12,
        },
      },
    },
    'BaseNode',
  );

  // register collapsed group
  g6.registerNode(
    COLLAPSED_GROUP_TYPE_G6_TYPE,
    {
      options: {
        size: COLLAPSED_GROUP_SIZE,
        style: {
          fill: Color.PrimaryWhite,
          lineWidth: 0,
        },
      },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      afterDraw: (cfg: G6.NodeConfig, group: G6.IGroup) => {
        drawCollapsedGroup(cfg, group);
        drawSelectedCollapsedGroup(cfg, group, COLLAPSED_GROUP_SIZE);
        drawHighlightedCollapsedGroup(cfg, group, COLLAPSED_GROUP_SIZE);
      },
      update: undefined,
    },
    'circle',
  );

  // register collapsed group
  g6.registerNode(
    COLLAPSED_PAIRWISE_NODE_TYPE_G6_TYPE,
    {
      options: {
        size: COLLAPSED_NODE_SIZE,
        style: {
          fill: Color.CoreTeal500,
          lineWidth: 0,
        },
      },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      afterDraw: (cfg: G6.NodeConfig, group: G6.IGroup) => {
        drawNode(cfg, group, nodeIconLookupFn);
        drawSelectedCollapsedGroup(cfg, group, COLLAPSED_NODE_SIZE);
        drawHighlightedCollapsedGroup(cfg, group, COLLAPSED_NODE_SIZE);
      },
      update: undefined,
    },
    'circle',
  );

  // register collapsed group
  g6.registerNode(
    COLLAPSED_PAIRWISE_NODE_TYPE_G6_TYPE,
    {
      options: {
        size: COLLAPSED_GROUP_SIZE,
        style: {
          fill: Color.CoreTeal500,
          lineWidth: 0,
        },
      },
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      afterDraw: (cfg: G6.NodeConfig, group: G6.IGroup) => {
        drawNode(cfg, group, nodeIconLookupFn);
        drawSelectedCollapsedGroup(cfg, group, COLLAPSED_GROUP_SIZE);
        drawHighlightedCollapsedGroup(cfg, group, COLLAPSED_GROUP_SIZE);
      },
      update: undefined,
    },
    'circle',
  );

  g6.registerNode(
    PROPERTY_NODE_TYPE_G6_TYPE,
    {
      options: {
        size: 24,
        style: {
          fill: UNKNOWN_COLOR,
          lineWidth: 0,
          radius: 8,
        },
      },
    },
    'BaseNode',
  );

  // Register a special type of edge, for transactions demo.
  g6.registerEdge(
    G6_DIRECTED_EDGE,
    {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      afterDraw(cfg: G6DirectedEdgeModelConfig, group: G6.IGroup) {
        // The first shape is the edge path.
        // See example at: https://g6.antv.antgroup.com/en/examples/item/customEdge/#extraShape
        const shape = group.get('children')[0];
        const startPoint = shape.getPoint(0);
        const endPoint = shape.getPoint(1);
        const directionVector = {
          x: endPoint.x - startPoint.x,
          y: endPoint.y - startPoint.y,
        };
        const directionVectorLength = Math.sqrt(
          directionVector.x * directionVector.x + directionVector.y * directionVector.y,
        );
        const directionVectorAngle = Math.atan2(directionVector.y, directionVector.x);

        const midPoint = shape.getPoint(0.5);
        // Offset the center point of the arrow, so that it looks (roughly) visually centered.
        const centerPoint = {
          x: midPoint.x - ((cfg.arrowSizeRadius / 2) * directionVector.x) / directionVectorLength,
          y: midPoint.y - ((cfg.arrowSizeRadius / 2) * directionVector.y) / directionVectorLength,
        };
        const points = [
          [
            centerPoint.x + cfg.arrowSizeRadius * Math.cos(directionVectorAngle),
            centerPoint.y + cfg.arrowSizeRadius * Math.sin(directionVectorAngle),
          ],
          [
            centerPoint.x +
              cfg.arrowSizeRadius * Math.cos(directionVectorAngle + (2 * Math.PI) / 3),
            centerPoint.y +
              cfg.arrowSizeRadius * Math.sin(directionVectorAngle + (2 * Math.PI) / 3),
          ],
          [centerPoint.x, centerPoint.y],
          [
            centerPoint.x +
              cfg.arrowSizeRadius * Math.cos(directionVectorAngle - (2 * Math.PI) / 3),
            centerPoint.y +
              cfg.arrowSizeRadius * Math.sin(directionVectorAngle - (2 * Math.PI) / 3),
          ],
        ];

        group.addShape('polygon', {
          attrs: {
            points: points,
            fill: Color.CoreGreen500,
          },
        });
      },
      update: undefined,
    },
    'linear',
  );
};

function drawNode(
  g6NodeConfig: G6.NodeConfig,
  group: G6.IGroup,
  nodeIconLookupFn: NodeIconLookupFn,
) {
  const node = g6NodeConfig.dataNode as Node;
  const nodeType = g6NodeConfig.type as G6Type;
  const imageSize = getImageSize(nodeType);
  // Render the icon for the node.
  const iconImageSrc =
    nodeType === datumTypeTokenToG6Type(ACCOUNT_DATUM_TYPE.token)
      ? V2_ICON_SOURCES.get(nodeType)
      : nodeIconLookupFn(node.id.token);

  if (iconImageSrc) {
    group.addShape('image', {
      attrs: {
        x: -imageSize / 2,
        y: -imageSize / 2,
        width: imageSize,
        height: imageSize,
        img: iconImageSrc,
      },
      name: 'iconImage',
    });
  }
}

function drawCollapsedGroup(g6NodeConfig: G6.NodeConfig, group: G6.IGroup) {
  const node = g6NodeConfig.dataNode as CollapsedGroup;

  const clusterSize = node.members.size;
  let text;
  if (clusterSize >= 100) {
    text = '99+';
  } else {
    text = clusterSize.toString();
  }

  const yOffset = COLLAPSED_GROUP_SIZE * 0.22;
  // for some reason, after we center the text, it looks offcentered to the right for each one in the text
  const numOnes = text.split('1').length - 1;
  const xOffset = numOnes * -1;

  group.addShape('text', {
    attrs: {
      text,
      fill: 'black',
      fontFamily: 'Rubik',
      fontWeight: 400,
      fontSize: COLLAPSED_GROUP_SIZE * 0.38,
      textAlign: 'center',
      width: COLLAPSED_GROUP_SIZE,
      x: xOffset,
      y: yOffset,
    },
    // must be assigned in G6 3.3 and later versions. it can be any value you want
    name: 'Collapsed Group Text',
  });
}

function drawHighlightedState(g6NodeConfig: G6.NodeConfig, group: G6.IGroup) {
  const node = g6NodeConfig.dataNode as Node;
  if (!node.state.highlighted) {
    return;
  }

  const nodeType = g6NodeConfig.type as G6Type;
  const imageSize = getImageSize(nodeType);
  // highlighted outline should be outside of the selected outline
  const highlightedSize = node.state.selected
    ? getOutlinedSize(imageSize) + 4.75
    : getOutlinedSize(imageSize);
  const posAttrs = {
    x: -highlightedSize / 2,
    y: -highlightedSize / 2,
    width: highlightedSize,
    height: highlightedSize,
  };
  group.addShape('rect', {
    attrs: {
      lineWidth: 3,
      stroke: node.state.dimmed ? Color.CorePurple100 : Color.CorePurple500,
      opacity: node.state.dimmed ? 0.5 : 1,
      radius: getRadius(nodeType),
      ...posAttrs,
    },
    name: 'Highlighted Outline',
  });
}

function drawHighlightedCollapsedGroup(
  g6NodeConfig: G6.NodeConfig,
  group: G6.IGroup,
  size: number,
) {
  const collapsedGroup = g6NodeConfig.dataNode as CollapsedGroup;
  if (!collapsedGroup.state.highlighted) {
    return;
  }

  const outlinedRadius = size / 2;
  // highlighted outline should be outside of the selected outline
  const highlightedRadius = collapsedGroup.state.selected ? outlinedRadius + 2.75 : outlinedRadius;
  group.addShape('circle', {
    attrs: {
      lineWidth: 3,
      stroke: collapsedGroup.state.dimmed ? Color.CorePurple100 : Color.CorePurple500,
      opacity: collapsedGroup.state.dimmed ? 0.5 : 1,
      r: highlightedRadius,
    },
    name: 'Highlighted Outline',
  });
}

function drawSelectedState(g6NodeConfig: G6.NodeConfig, group: G6.IGroup) {
  const node = g6NodeConfig.dataNode as Node;
  if (!node.state.selected) {
    return;
  }

  const nodeType = g6NodeConfig.type as G6Type;
  const imageSize = getImageSize(nodeType);
  const selectedSize = getOutlinedSize(imageSize);
  group.addShape('rect', {
    attrs: {
      lineWidth: 3,
      stroke: node.state.dimmed ? Color.CorePurple200 : Color.CorePurple700,
      opacity: node.state.dimmed ? 0.5 : 1,
      x: -selectedSize / 2,
      y: -selectedSize / 2,
      width: selectedSize,
      height: selectedSize,
      radius: getRadius(nodeType),
    },
    name: 'Selected Outer Outline',
  });
  group.addShape('rect', {
    attrs: {
      lineWidth: 0.5,
      stroke: Color.PrimaryWhite,
      x: -selectedSize / 2 + 1.5,
      y: -selectedSize / 2 + 1.5,
      width: selectedSize - 3.2,
      height: selectedSize - 3.2,
      radius: getRadius(nodeType),
    },
    name: 'Selected Inner Outline',
  });
}

function drawSelectedCollapsedGroup(g6NodeConfig: G6.NodeConfig, group: G6.IGroup, size: number) {
  const collapsedGroup = g6NodeConfig.dataNode as CollapsedGroup | CollapsedPairwiseNode;
  if (!collapsedGroup.state.selected) {
    return;
  }

  // highlighted outline should be outside of the selected outline
  const selectedRadius = size / 2;
  group.addShape('circle', {
    attrs: {
      lineWidth: 3,
      stroke: collapsedGroup.state.dimmed ? Color.CorePurple200 : Color.CorePurple700,
      opacity: collapsedGroup.state.dimmed ? 0.5 : 1,
      r: selectedRadius,
    },
    name: 'Selected Outer Outline',
  });
  group.addShape('circle', {
    attrs: {
      lineWidth: 0.5,
      stroke: Color.PrimaryWhite,
      r: selectedRadius - 1.6,
    },
    name: 'Selected Inner Outline',
  });
}

function drawLoadingState(g6NodeConfig: G6.NodeConfig, group: G6.IGroup) {
  const node = g6NodeConfig.dataNode as Node;
  if (!node.state.loading) {
    return;
  }

  // Top right corner
  const { maxX: x, minY: y } = group.find((el) => el.get('className') === 'node-shape').getBBox();

  const badge = group.addGroup({
    id: 'badge',
  });

  badge.translate(x - 2, y + 2);

  badge.addShape('circle', {
    attrs: {
      stroke: Color.PrimaryWhite,
      fill: Color.CorePurple600,
      r: BADGE_RADIUS,
    },
    name: 'Loading Badge',
  });

  const loadingShape = badge.addShape('path', {
    attrs: {
      path: [
        ['M', -2, -2],
        ['A', 1, 1, 1, 1, 1, 2, 2],
      ],
      stroke: Color.PrimaryWhite,
    },
    name: 'Loading Path',
  });

  loadingShape.animate(
    (ratio: number) => {
      // Returns the properties for each frame.
      // The input parameter ratio is a number that range from 0 to 1.
      // The return value is an object that defines the properties for this frame.
      // Rotate by manipulating matrix The current matrix
      const matrix = [1, 0, 0, 0, 1, 0, 0, 0, 1];
      // The target matrix
      const toMatrix = G6.Util.transform(matrix, [['r', ratio * Math.PI * 2]]);
      // The properties of this frame. Only target matrix for this demo
      return {
        matrix: toMatrix,
      };
    },
    {
      repeat: true,
      duration: 1000,
      easing: 'easeLinear',
      delay: 0,
    },
  );
}

function drawExpandableState(g6NodeConfig: G6.NodeConfig, group: G6.IGroup) {
  const node = g6NodeConfig.dataNode as Node;
  if (!node.state.nonfetchedEdgeCount || node.state.loading) {
    return;
  }

  // Top right corner
  const { maxX: x, minY: y } = group.find((el) => el.get('className') === 'node-shape').getBBox();

  const nonfetchedEdgeCount = node.state.nonfetchedEdgeCount;
  let extraWidth = 0;
  let text = nonfetchedEdgeCount.toString();

  // do not show unfetched edge count on account nodes
  if (node.id.token === ACCOUNT_DATUM_TYPE.token) {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    text = null;
  }
  // display 99+ if nonfectehd edge count is triple digits (or more)
  else if (nonfetchedEdgeCount >= 100) {
    extraWidth = 2 * BADGE_RADIUS * 0.8;
    text = '99+';
  }
  // resize for double digits
  else if (nonfetchedEdgeCount >= 10) {
    extraWidth = BADGE_RADIUS * 0.8;
  }

  // draw and position the badge
  const badge = group.addGroup({
    id: 'badge',
  });
  const badgeWidth = BADGE_RADIUS * 2 + extraWidth;
  const badgeHeight = BADGE_RADIUS * 2;
  badge.addShape('rect', {
    attrs: {
      stroke: Color.PrimaryWhite,
      fill: node.state.dimmed ? Color.CoreSlate50 : Color.BorderIconActive,
      width: badgeWidth,
      height: badgeHeight,
      radius: BADGE_RADIUS,
    },
    name: 'Expandable Badge Inner',
  });
  const badgeOffsetFromNodeTR = {
    x: -2,
    y: 2,
  };
  badge.translate(
    x - badgeWidth * 0.5 + badgeOffsetFromNodeTR.x,
    y - badgeHeight * 0.5 + badgeOffsetFromNodeTR.y,
  );

  // draw and position the badge text
  if (text) {
    const badgeText = badge.addShape('text', {
      attrs: {
        text,
        fill: 'white',
        fontWeight: 400,
        fontSize: BADGE_RADIUS * 1.5,
      },
      // must be assigned in G6 3.3 and later versions. it can be any value you want
      name: 'Expandable Badge Text',
    });
    const badgeTextOffsetFromBadgeTL = {
      x: BADGE_RADIUS * 0.6,
      y: BADGE_RADIUS * 1.8,
    };
    badgeText.translate(badgeTextOffsetFromBadgeTL.x, badgeTextOffsetFromBadgeTL.y);
  }
}

function drawExcludedState(g6NodeConfig: G6.NodeConfig, group: G6.IGroup) {
  const node = g6NodeConfig.dataNode as Node;
  if (!node.state.excluded) {
    return;
  }

  // Top right corner
  const { maxX: x, minY: y } = group.find((el) => el.get('className') === 'node-shape').getBBox();

  // draw and position the badge
  const badge = group.addGroup({
    id: 'badge',
  });
  const badgeWidth = BADGE_RADIUS * 2;
  const badgeHeight = BADGE_RADIUS * 2;
  badge.addShape('rect', {
    attrs: {
      stroke: Color.PrimaryWhite,
      fill: Color.CoreSlate800,
      width: badgeWidth,
      height: badgeHeight,
      radius: BADGE_RADIUS,
    },
    name: 'Excluded Badge Inner',
  });
  const badgeOffsetFromNodeTR = {
    x: -2,
    y: 2,
  };
  badge.translate(
    x - badgeWidth * 0.5 + badgeOffsetFromNodeTR.x,
    y - badgeHeight * 0.5 + badgeOffsetFromNodeTR.y,
  );

  // draw and position the badge text
  const icon = badge.addShape('rect', {
    attrs: {
      fill: 'white',
      width: BADGE_RADIUS * 0.9,
      height: 1,
    },
    // must be assigned in G6 3.3 and later versions. it can be any value you want
    name: 'Excluded Badge Text',
  });
  const offset = {
    x: BADGE_RADIUS * 0.525,
    y: BADGE_RADIUS * 0.9,
  };
  icon.translate(offset.x, offset.y);
}
